if (!customElements.get('lp-carousel-controls')) {
  customElements.define(
    'lp-carousel-controls',
    class LpCarouselControls extends HTMLElement {
      connectedCallback() {
        this.prevBtn = this.querySelector('.slider-arrow.prev');
        this.nextBtn = this.querySelector('.slider-arrow.next');
        this.progressBar = this.querySelector('.progress-bar-fill');

        const targetSelector = this.getAttribute('data-target');
        if (!targetSelector) return;

        this.waitForTarget(targetSelector).then((target) => {
          this.track = target;
          this.init();
        });
      }

      waitForTarget(selector) {
        return new Promise((resolve) => {
          const target = document.querySelector(selector);
          if (target) return resolve(target);

          const observer = new MutationObserver((mutations, obs) => {
            const el = document.querySelector(selector);
            if (el) {
              obs.disconnect();
              resolve(el);
            }
          });

          observer.observe(document.body, { childList: true, subtree: true });
        });
      }

      init() {
        const track = this.track;
        const originalSlides = Array.from(
          track.querySelectorAll('.lp-carousel__slide:not(.lp-carousel__slide--clone)')
        );
        const slideCount = originalSlides.length;

        if (slideCount < 2) {
          this.updateProgress();
          return;
        }

        this.slideCount = slideCount;
        this.isIOSSafari =
          /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

        const isDesktop = window.matchMedia('(min-width: 48em)').matches;
        const hasEmptyContent =
          track.querySelector('.lp-carousel__slide--content-empty') !== null;
        this.needsExtraClones = hasEmptyContent && isDesktop;

        this.cloneSlides(originalSlides);

        this.allSlides = Array.from(
          track.querySelectorAll('.lp-carousel__slide')
        );
        this.firstRealIndex = this.needsExtraClones ? 3 : 1;
        this.lastRealIndex =
          this.allSlides.length - (this.needsExtraClones ? 4 : 2);

        this.initEvents();

        requestAnimationFrame(() => {
          this.jumpToSlide(this.firstRealIndex);
          this.reEnableScrollSnap();
          this.updateProgress();
        });
      }

      cloneSlides(originalSlides) {
        const track = this.track;
        const count = originalSlides.length;

        if (!this.needsExtraClones) {
          const firstClone = originalSlides[0].cloneNode(true);
          const lastClone = originalSlides[count - 1].cloneNode(true);
          firstClone.classList.add('lp-carousel__slide--clone');
          lastClone.classList.add('lp-carousel__slide--clone');
          firstClone.removeAttribute('data-block-id');
          lastClone.removeAttribute('data-block-id');
          track.appendChild(firstClone);
          track.insertBefore(lastClone, originalSlides[0]);
        } else {
          const clonesPerSide = 3;
          for (let i = 0; i < clonesPerSide; i++) {
            const clone = originalSlides[count - 1 - i].cloneNode(true);
            clone.classList.add('lp-carousel__slide--clone');
            clone.removeAttribute('data-block-id');
            track.insertBefore(clone, track.firstChild);
          }
          for (let i = 0; i < clonesPerSide; i++) {
            const clone = originalSlides[i].cloneNode(true);
            clone.classList.add('lp-carousel__slide--clone');
            clone.removeAttribute('data-block-id');
            track.appendChild(clone);
          }
        }
      }

      initEvents() {
        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.navigate(-1));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.navigate(1));
        }

        this._handleScroll = this.handleScroll.bind(this);
        this.track.addEventListener('scroll', this._handleScroll, {
          passive: true,
        });

        this.initDrag();

        this._handleResize = this.debounce(() => this.updateProgress(), 100);
        window.addEventListener('resize', this._handleResize);
      }

      disconnectedCallback() {
        if (this.track && this._handleScroll) {
          this.track.removeEventListener('scroll', this._handleScroll);
        }
        if (this._handleResize) {
          window.removeEventListener('resize', this._handleResize);
        }
      }

      getTargetScrollLeft(slide) {
        return (
          slide.offsetLeft -
          (this.track.clientWidth - slide.offsetWidth) / 2
        );
      }

      getCurrentSlideIndex() {
        const scrollLeft = this.track.scrollLeft;
        let closestIndex = 0;
        let closestDistance = Infinity;

        for (const [index, slide] of this.allSlides.entries()) {
          const target = this.getTargetScrollLeft(slide);
          const distance = Math.abs(scrollLeft - target);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = index;
          }
        }

        return closestIndex;
      }

      isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      }

      jumpToSlide(index, behavior = 'instant') {
        const slide = this.allSlides[index];
        if (!slide) return;

        const targetLeft = this.getTargetScrollLeft(slide);

        if (behavior === 'instant' && this.isTouchDevice()) {
          this.track.style.scrollSnapType = 'none';
          this.track.style.scrollBehavior = 'auto';
          this.track.scrollTo({ left: targetLeft, behavior: 'instant' });
          if (this.isIOSSafari) this.track.offsetHeight;
        } else {
          this.track.scrollTo({ left: targetLeft, behavior: behavior });
        }
      }

      reEnableScrollSnap() {
        if (!this.isTouchDevice()) return;

        if (this.isIOSSafari) {
          setTimeout(() => {
            this.track.style.scrollSnapType = '';
            this.track.style.scrollBehavior = '';
          }, 100);
        } else {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.track.style.scrollSnapType = '';
              this.track.style.scrollBehavior = '';
            });
          });
        }
      }

      navigate(direction) {
        const currentIndex = this.getCurrentSlideIndex();

        if (direction === -1 && currentIndex <= this.firstRealIndex) {
          const jumpTo = this.allSlides.length - (this.needsExtraClones ? 4 : 2) + 1;
          this.jumpToSlide(jumpTo);
          requestAnimationFrame(() => {
            this.jumpToSlide(this.lastRealIndex, 'smooth');
            this.reEnableScrollSnap();
          });
        } else if (direction === 1 && currentIndex >= this.lastRealIndex) {
          const jumpTo = this.needsExtraClones ? 2 : 0;
          this.jumpToSlide(jumpTo);
          requestAnimationFrame(() => {
            this.jumpToSlide(this.firstRealIndex, 'smooth');
            this.reEnableScrollSnap();
          });
        } else {
          const targetIndex = currentIndex + direction;
          if (
            targetIndex >= this.firstRealIndex &&
            targetIndex <= this.lastRealIndex
          ) {
            this.jumpToSlide(targetIndex, 'smooth');
          }
        }
      }

      handleScroll() {
        clearTimeout(this._scrollEndTimer);
        const delay = this.isIOSSafari ? 150 : 30;

        this._scrollEndTimer = setTimeout(() => {
          this.updateProgress();

          const currentIndex = this.getCurrentSlideIndex();
          const totalSlides = this.allSlides.length;
          let scrollAdjustment = 0;

          if (!this.needsExtraClones) {
            if (currentIndex === 0) {
              scrollAdjustment =
                this.allSlides[this.slideCount].offsetLeft -
                this.allSlides[0].offsetLeft;
            } else if (currentIndex === totalSlides - 1) {
              scrollAdjustment =
                this.allSlides[1].offsetLeft -
                this.allSlides[totalSlides - 1].offsetLeft;
            }
          } else {
            if (currentIndex < 3) {
              scrollAdjustment =
                this.allSlides[currentIndex + this.slideCount].offsetLeft -
                this.allSlides[currentIndex].offsetLeft;
            } else if (currentIndex > totalSlides - 4) {
              scrollAdjustment =
                this.allSlides[currentIndex - this.slideCount].offsetLeft -
                this.allSlides[currentIndex].offsetLeft;
            }
          }

          if (scrollAdjustment !== 0) {
            if (this.isTouchDevice()) {
              this.track.style.scrollSnapType = 'none';
              this.track.style.scrollBehavior = 'auto';
            }
            this.track.scrollTo({
              left: this.track.scrollLeft + scrollAdjustment,
              behavior: 'instant',
            });
            if (this.isIOSSafari) this.track.offsetHeight;
            this.reEnableScrollSnap();
          }
        }, delay);
      }

      updateProgress() {
        if (!this.progressBar || !this.track) return;

        if (!this.allSlides || this.allSlides.length === 0) {
          const scrollLeft = this.track.scrollLeft;
          const scrollWidth = this.track.scrollWidth;
          const clientWidth = this.track.clientWidth;
          const percentage =
            scrollWidth > clientWidth
              ? ((scrollLeft + clientWidth) / scrollWidth) * 100
              : 100;
          this.progressBar.style.width = percentage + '%';
          return;
        }

        const currentIndex = this.getCurrentSlideIndex();
        const realIndex = currentIndex - this.firstRealIndex;
        const realCount = this.slideCount;
        const percentage = ((realIndex + 1) / realCount) * 100;
        this.progressBar.style.width =
          Math.max(0, Math.min(100, percentage)) + '%';
      }

      initDrag() {
        let isDragging = false;
        let startX = 0;
        let scrollLeftStart = 0;

        this.track.addEventListener('mousedown', (e) => {
          isDragging = true;
          startX = e.pageX - this.track.offsetLeft;
          scrollLeftStart = this.track.scrollLeft;
          this.track.style.scrollBehavior = 'auto';
        });

        this.track.addEventListener('mousemove', (e) => {
          if (!isDragging) return;
          e.preventDefault();
          const x = e.pageX - this.track.offsetLeft;
          const walk = (startX - x) * 1.5;
          this.track.scrollLeft = scrollLeftStart + walk;
        });

        this.track.addEventListener('mouseup', () => {
          isDragging = false;
          this.track.style.scrollBehavior = 'smooth';
        });

        this.track.addEventListener('mouseleave', () => {
          isDragging = false;
          this.track.style.scrollBehavior = 'smooth';
        });
      }

      debounce(fn, delay) {
        let timer;
        return (...args) => {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), delay);
        };
      }
    }
  );
}
